/* eslint-disable import/prefer-default-export */
import { useEffect, useRef } from 'react'

/**
 * Auto-logout on inactivity + absolute session cap. Wire once near the app
 * root (inside AuthGuard is a good place — it only runs for authenticated
 * users).
 *
 * Defaults:
 *  - idleMs: 15 min of no user input → logout
 *  - maxSessionMs: 8 h after login → logout regardless of activity
 */
const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'touchstart',
  'scroll',
  'visibilitychange'
] as const

interface Options {
  idleMs?: number
  maxSessionMs?: number
  onTimeout: (reason: 'idle' | 'absolute') => void
}

export function useIdleTimeout({
  idleMs = 30 * 60 * 1000,
  maxSessionMs = 8 * 60 * 60 * 1000,
  onTimeout
}: Options): void {
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const absoluteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const resetIdle = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
      idleTimerRef.current = setTimeout(() => onTimeout('idle'), idleMs)
    }

    absoluteTimerRef.current = setTimeout(
      () => onTimeout('absolute'),
      maxSessionMs
    )
    resetIdle()
    ACTIVITY_EVENTS.forEach((ev) =>
      window.addEventListener(ev, resetIdle, { passive: true })
    )

    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, resetIdle))
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
      if (absoluteTimerRef.current) clearTimeout(absoluteTimerRef.current)
    }
  }, [idleMs, maxSessionMs, onTimeout])
}
